import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, CalendarClock, Home, Wrench } from "lucide-react";
import { AppHeader } from "@/components/site/AppHeader";
import { listManagedHomeCalendar } from "@/lib/homes.functions";
import { ukDate } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/home/calendar")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Home calendar — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HomeCalendarPage,
});

function HomeCalendarPage() {
  const { homeId } = Route.useSearch();
  const calendar = useQuery({
    queryKey: ["managed-home-calendar"],
    queryFn: () => listManagedHomeCalendar(),
  });

  const rows = [
    ...(calendar.data?.bookings ?? []).filter((item) => item.date && (!homeId || item.homeId === homeId)).map((item) => ({
      id: "booking-" + item.id,
      date: item.date as string,
      type: "job" as const,
      title: item.serviceName,
      detail: item.homeLabel + " · " + String(item.status).replaceAll("_", " ") + (item.window ? " · " + item.window : ""),
      bookingId: item.id,
      canOpen: item.canOpenBooking,
      shared: item.shared,
    })),
    ...(calendar.data?.reminders ?? []).filter((item: { homeId: string }) => !homeId || item.homeId === homeId).map((item: { id: string; dueDate: string; title: string; homeLabel: string; source: string; shared: boolean }) => ({
      id: "reminder-" + item.id,
      date: item.dueDate,
      type: "reminder" as const,
      title: item.title,
      detail: item.homeLabel + " · " + (item.source === "customer_set" ? "date set by customer" : "recorded service date"),
      bookingId: "",
      canOpen: false,
      shared: item.shared,
    })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>
        <header className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">My Home</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">Home Calendar</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            {homeId ? "Scheduled work and recorded reminders for this home." : "Scheduled Shields work and evidence-backed reminders across your properties. Family sharing adds dates from homes shared with you."}
          </p>
        </header>

        {calendar.isLoading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading your home calendar…</p>
        ) : calendar.isError ? (
          <section className="card-surface mt-8 p-8 text-center">
            <CalendarClock className="mx-auto h-8 w-8 text-destructive" />
            <h2 className="mt-3 font-display text-lg font-bold">We couldn’t load your home calendar</h2>
            <p className="mt-1 text-sm text-muted-foreground">Try again. Nothing in your home history has been changed.</p>
            <button type="button" onClick={() => calendar.refetch()} className="btn-secondary-hero mt-5">
              Try again
            </button>
          </section>
        ) : rows.length === 0 ? (
          <section className="card-surface mt-8 p-8 text-center">
            <CalendarClock className="mx-auto h-8 w-8 text-primary" />
            <h2 className="mt-3 font-display text-lg font-bold">Nothing scheduled yet</h2>
            <p className="mt-1 text-sm text-muted-foreground">Booked work and evidence-backed reminder dates will appear here automatically.</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link to="/book" className="btn-hero inline-flex">Book a service</Link>
              <Link to="/home/maintenance" search={{ homeId }} className="btn-secondary-hero inline-flex">Add a reminder</Link>
            </div>
          </section>
        ) : (
          <section className="mt-8 space-y-3">
            {rows.map((row) => {
              const card = (
                <article className="flex items-center gap-4 rounded-2xl border border-[#e1e8f0] bg-white p-4">
                  <span className="reference-row-icon">{row.type === "job" ? <Wrench /> : <CalendarClock />}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <strong>{row.title}</strong>
                      {row.shared && <span className="locked-feature"><Home className="h-3.5 w-3.5" /> Shared home</span>}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{ukDate(row.date)} · {row.detail}</p>
                  </div>
                </article>
              );
              return row.canOpen ? (
                <Link key={row.id} to="/bookings/$bookingId" params={{ bookingId: row.bookingId }}>{card}</Link>
              ) : (
                <div key={row.id}>{card}</div>
              );
            })}
          </section>
        )}
      </main>
    </div>
  );
}
